// Cliente HTTP del portal: un solo endpoint POST JSON {action, payload} con
// Bearer, igual que src/data/FirebaseBackend.ts de la app — pero sin llavero ni
// refresh: el portal vive del token de sesión propio (cst_…) en sessionStorage.

export const API_URL = (import.meta.env.VITE_API_URL as string | undefined)
  ?? 'https://api.cargados.mx/demo-cargados/us-central1/cargadosApi';
export const AUTH_URL = (import.meta.env.VITE_AUTH_URL as string | undefined)
  ?? 'https://auth.cargados.mx/identitytoolkit.googleapis.com/v1';
// El emulador de Auth acepta cualquier API key; la de la app es 'emulator-only'.
export const AUTH_KEY = (import.meta.env.VITE_AUTH_KEY as string | undefined) ?? 'fake-key';

export class ApiError extends Error {
  constructor(public code: string, message: string, public status = 0) { super(message); this.name = 'ApiError'; }
}

// Mensajes del login de Firebase/Auth-emulator (mismos que la app).
const AUTH_MESSAGES: Record<string, string> = {
  EMAIL_EXISTS: 'Ya existe una cuenta con este correo.',
  INVALID_LOGIN_CREDENTIALS: 'Revisa tu correo y contraseña.',
  INVALID_PASSWORD: 'Revisa tu correo y contraseña.',
  EMAIL_NOT_FOUND: 'Revisa tu correo y contraseña.',
  WEAK_PASSWORD: 'Usa una contraseña de al menos 12 caracteres.',
  TOO_MANY_ATTEMPTS_TRY_LATER: 'Espera unos minutos antes de intentar de nuevo.',
  USER_DISABLED: 'Esta cuenta está deshabilitada.',
  INVALID_ID_TOKEN: 'Vuelve a entrar para renovar tu sesión de verificación.',
  INVALID_REFRESH_TOKEN: 'Tu sesión venció. Inicia sesión nuevamente.',
  TOKEN_EXPIRED: 'Tu sesión venció. Inicia sesión nuevamente.',
};
// La API responde {error:{code,message}}; el Auth emulator responde
// {error:{code:<número HTTP>,message:'EMAIL_NOT_FOUND'}} — la forma del código
// cambia entre los dos, aquí se normaliza a un solo ApiError.
function toError(body: Record<string, unknown>, status: number): ApiError {
  const err = (body?.error ?? {}) as Record<string, unknown>;
  const raw = typeof err.code === 'string' ? err.code : String(err.message ?? 'REQUEST_FAILED').split(' : ')[0];
  const message = AUTH_MESSAGES[raw] ?? (typeof err.message === 'string' && typeof err.code === 'string' ? err.message : 'No se pudo completar la solicitud.');
  return new ApiError(raw, message, status);
}

async function request(url: string, init: RequestInit, timeoutMs = 15000): Promise<Record<string, unknown>> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { ...init, signal: controller.signal });
    let body: Record<string, unknown>;
    try { body = await response.json(); } catch { throw new ApiError('INVALID_RESPONSE', 'El servicio no respondió correctamente.', response.status); }
    if (!response.ok) throw toError(body, response.status);
    return body;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('NETWORK', 'No hay conexión con el servicio. Tus cambios no se han confirmado.');
  } finally {
    clearTimeout(timer);
  }
}

/** POST al endpoint único de la API. Sin `token` la llamada es anónima;
    `timeoutMs` sube a 60 s en acciones que llevan foto embebida (igual que la app). */
export async function api<T>(action: string, payload: Record<string, unknown> = {}, token?: string, timeoutMs = 15000): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const body = await request(API_URL, { method: 'POST', headers, body: JSON.stringify({ action, payload }) }, timeoutMs);
  return body.data as T;
}

/**
 * Las fotos (de lugares, visitas y documentos) no tienen URL pública: el
 * servicio las sirve por GET {API_URL}?media=<ref> con Bearer. El navegador no
 * puede poner headers en <img>/<a>, así que se descarga como blob y se abre con
 * una URL local temporal. Devuelve el blob URL (el llamador hace revokeObjectURL
 * si la incrusta; si la abre en pestaña nueva la libera el navegador).
 */
export async function mediaBlobUrl(ref: string, token: string): Promise<string> {
  const media = ref.startsWith('media:') ? ref.slice('media:'.length) : ref;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(`${API_URL}?media=${encodeURIComponent(media)}`, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal });
    if (!response.ok) {
      let body: Record<string, unknown> = {};
      try { body = await response.json(); } catch { /* cuerpo no-JSON */ }
      throw toError(body, response.status);
    }
    return URL.createObjectURL(await response.blob());
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('NETWORK', 'No hay conexión con el servicio. Tus cambios no se han confirmado.');
  } finally {
    clearTimeout(timer);
  }
}

export interface FirebaseSignIn { idToken: string; refreshToken: string; expiresIn: string }

/** accounts:signInWithPassword del Auth emulator (o de Firebase real). */
export async function signInWithPassword(email: string, password: string): Promise<FirebaseSignIn> {
  const body = await request(
    `${AUTH_URL}/accounts:signInWithPassword?key=${encodeURIComponent(AUTH_KEY)}`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password, returnSecureToken: true }) },
  );
  return body as unknown as FirebaseSignIn;
}

/** accounts:signUp del Auth emulator (o de Firebase real) — alta de la cuenta
    con correo y contraseña; devuelve el idToken que autoriza lo demás. */
export async function signUpAccount(email: string, password: string): Promise<FirebaseSignIn> {
  const body = await request(
    `${AUTH_URL}/accounts:signUp?key=${encodeURIComponent(AUTH_KEY)}`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password, returnSecureToken: true }) },
  );
  return body as unknown as FirebaseSignIn;
}

/** accounts:update — fija el displayName de la cuenta recién creada. Puede
    devolver tokens nuevos (el llamador los usa si llegan). */
export async function updateAccount(idToken: string, displayName: string): Promise<Partial<FirebaseSignIn>> {
  const body = await request(
    `${AUTH_URL}/accounts:update?key=${encodeURIComponent(AUTH_KEY)}`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ idToken, displayName, returnSecureToken: true }) },
  );
  return body as unknown as Partial<FirebaseSignIn>;
}

/** accounts:sendOobCode — los correos nativos de Auth: VERIFY_EMAIL (necesita el
    idToken de la cuenta) y PASSWORD_RESET (con el correo). Es el camino de la
    nube: las acciones de código del servicio solo existen cuando el despliegue
    trae correo propio (emulador/selfhost); si no, responden INVALID_ACTION. */
export async function sendOobCode(payload: { requestType: 'VERIFY_EMAIL'; idToken: string } | { requestType: 'PASSWORD_RESET'; email: string }): Promise<void> {
  await request(
    `${AUTH_URL}/accounts:sendOobCode?key=${encodeURIComponent(AUTH_KEY)}`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) },
  );
}

// Respaldo legible para códigos del servicio: la API ya manda el mensaje en
// español y se respeta; esto cubre respuestas donde el mensaje llega vacío o
// es el código crudo (despliegue viejo, proxy intermedio, etc.).
const API_MESSAGES: Record<string, string> = {
  INVALID_CODE: 'El código no es válido.',
  COMPANY_REQUIRED: 'Primero únete a una empresa con su código de invitación.',
  COMPANY_PENDING: 'La empresa todavía espera autorización del administrador.',
  COMPANY_BLOCKED: 'La empresa está suspendida. Avisa al administrador.',
  TOO_MANY_ATTEMPTS: 'Demasiados intentos. Espera unos minutos para volver a probar.',
  NOT_CONFIGURED: 'El servicio aún no está configurado. Avisa al administrador.',
};

/** Mensaje visible: traduce códigos que el portal conoce y respeta los del servidor. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'INVALID_ACTION') return 'El servidor aún no tiene esta función. Actualiza el despliegue del backend.';
    if (error.code === 'NETWORK') return 'No hay conexión con el servicio. Revisa tu red e inténtalo de nuevo.';
    if (!error.message || error.message === error.code) return API_MESSAGES[error.code] ?? 'No se pudo completar la solicitud.';
    return error.message;
  }
  return 'No se pudo completar la solicitud.';
}

/** Errores que invalidan la sesión local (mismo criterio que la app). */
export function isAuthFailure(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.code.startsWith('MEMBERSHIP_')
    || ['UNAUTHENTICATED', 'BLOCKED', 'SESSION_REQUIRED', 'TOKEN_EXPIRED', 'INVALID_REFRESH_TOKEN', 'USER_DISABLED', 'USER_NOT_FOUND'].includes(error.code));
}
